// hooks/use-resumable-ws.ts
//
// Shared WebSocket lifecycle for stage components that subscribe to a
// single server-pushed run (Stage 4 measurement, Stage 5 scoring).
//
// Stage 4 and Stage 5 used to re-implement the same five-step dance:
//   1. open a WS via the API-level factory
//   2. wire onmessage → dispatch to a big handler
//   3. wire onclose → null the ref + REST-poll for the authoritative state
//   4. close the existing WS before reconnecting (resume flow, restart flow)
//   5. close on unmount
// This hook owns steps 1-3 and 5. The parent declares whether a
// connection should exist via `connectionKey` (typically a run-id, or
// null when no live run is in progress). When the key changes the hook
// tears down the previous socket and opens a new one.
//
// Stage 10 — which juggles N concurrent per-row WebSockets — does not
// use this hook on purpose.

import { useEffect, useRef } from "react";

export interface ResumableWsOpts<TMessage> {
  /**
   * Identity of the connection that should be open. Null means
   * "don't connect". When the value changes the old socket is closed
   * and a new one is opened.
   */
  connectionKey: string | number | null;

  /**
   * Factory called by the hook to open the WebSocket. The hook will
   * overwrite onmessage/onclose on the returned socket — the parent
   * does not need to wire them.
   */
  openWs: () => WebSocket;

  /** Parsed message dispatcher. Malformed payloads are dropped. */
  onMessage: (msg: TMessage) => void;

  /**
   * Called when the socket closes for any reason other than the hook
   * itself tearing it down on key-change / unmount. Use this for the
   * REST resync fallback (the "WS died, run finished server-side,
   * UI is now lying" case).
   */
  onClose?: () => void;

  /** Optional error sink. */
  onError?: (e: Event) => void;
}

/**
 * Lifecycle-managed WebSocket subscription.
 *
 * Behavioural notes preserved from the original Stage 4/5 implementations:
 *   - The socket is closed when the component unmounts.
 *   - Switching connectionKey triggers a clean re-open (close → open).
 *   - The onClose callback fires only on _unexpected_ closes — explicit
 *     tear-downs (unmount, key change) suppress it so callers don't
 *     run a resync against a connection they just closed themselves.
 */
export function useResumableWs<TMessage>({
  connectionKey,
  openWs,
  onMessage,
  onClose,
  onError,
}: ResumableWsOpts<TMessage>): void {
  // Callbacks captured by ref so identity changes in the parent don't
  // tear down and re-open the socket on every render.
  const cbRef = useRef({ onMessage, onClose, onError });
  cbRef.current = { onMessage, onClose, onError };

  useEffect(() => {
    if (connectionKey == null) return;

    let teardown = false;
    const ws = openWs();

    ws.onmessage = (e) => {
      try {
        cbRef.current.onMessage(JSON.parse(e.data) as TMessage);
      } catch {
        /* drop malformed payload — same behaviour as the per-stage code */
      }
    };
    ws.onclose = () => {
      if (teardown) return;             // suppress onClose for our own teardown
      cbRef.current.onClose?.();
    };
    ws.onerror = (e) => {
      cbRef.current.onError?.(e);
    };

    return () => {
      teardown = true;
      try {
        ws.close();
      } catch {
        /* socket already closed */
      }
    };
    // openWs is intentionally not in the dep array — it's a factory
    // and would be re-created every render in the parent. The
    // connectionKey gives us the right invalidation signal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [connectionKey]);
}
